import { useState, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowRightLeft, CheckCircle, XCircle, Clock, Award, BookOpen, Building2 } from 'lucide-react';

const PendingTransfers = () => {
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showRejectionDialog, setShowRejectionDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPendingTransfers();
  }, []);

  const loadPendingTransfers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/companies/pending-transfers');
      setPendingTransfers(response.data.transfers || []);
    } catch (error) {
      console.error('Error loading pending transfers:', error);
      toast.error('Failed to load pending transfers');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedTransfer) return;

    setProcessing(true);
    try {
      await axiosInstance.post(`/companies/approve-transfer/${selectedTransfer.id}`);
      
      toast.success(
        <div>
          <div className="font-semibold">Transfer Approved!</div>
          <div className="text-sm">{selectedTransfer.workerName} has been added to your company</div>
        </div>
      );
      
      setShowApprovalDialog(false);
      setSelectedTransfer(null);
      loadPendingTransfers();
    } catch (error) {
      console.error('Error approving transfer:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to approve transfer';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedTransfer || !rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    try {
      await axiosInstance.post(`/companies/reject-transfer/${selectedTransfer.id}`, {
        reason: rejectionReason.trim()
      });
      
      toast.success('Transfer request rejected');
      
      setShowRejectionDialog(false);
      setSelectedTransfer(null);
      setRejectionReason('');
      loadPendingTransfers();
    } catch (error) {
      console.error('Error rejecting transfer:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to reject transfer';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Pending Worker Transfers
          </CardTitle>
          <CardDescription>Loading...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (pendingTransfers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Pending Worker Transfers
          </CardTitle>
          <CardDescription>No pending transfer requests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>You have no pending worker transfer requests</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Pending Worker Transfers
            <Badge variant="secondary">{pendingTransfers.length}</Badge>
          </CardTitle>
          <CardDescription>
            Review and approve incoming worker transfers with their training records
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {pendingTransfers.map((transfer) => (
              <div
                key={transfer.id}
                className="border border-slate-200 rounded-lg p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg text-slate-900">
                        {transfer.workerName}
                      </h3>
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                      <div>
                        <span className="text-slate-600">Email:</span>
                        <span className="ml-2 font-medium">{transfer.workerEmail}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Role:</span>
                        <span className="ml-2 font-medium capitalize">{transfer.workerRole}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-600">From:</span>
                        <span className="ml-2 font-medium">{transfer.sourceCompanyName}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Requested:</span>
                        <span className="ml-2 font-medium">
                          {new Date(transfer.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-sm bg-white p-3 rounded border border-slate-200">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-slate-700">
                          {transfer.certificatesCount}
                        </span>
                        <span className="text-slate-600">Certificates</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-blue-600" />
                        <span className="font-semibold text-slate-700">
                          {transfer.progressRecordsCount}
                        </span>
                        <span className="text-slate-600">Training Records</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      onClick={() => {
                        setSelectedTransfer(transfer);
                        setShowApprovalDialog(true);
                      }}
                      className="bg-green-600 hover:bg-green-700"
                      size="sm"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve & Import
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedTransfer(transfer);
                        setShowRejectionDialog(true);
                      }}
                      size="sm"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              Approve Worker Transfer
            </DialogTitle>
            <DialogDescription>
              Confirm that you want to import this worker and their training records
            </DialogDescription>
          </DialogHeader>

          {selectedTransfer && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2">Worker Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-600">Name:</span>
                    <span className="ml-2 font-medium">{selectedTransfer.workerName}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Email:</span>
                    <span className="ml-2 font-medium">{selectedTransfer.workerEmail}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Role:</span>
                    <span className="ml-2 font-medium capitalize">{selectedTransfer.workerRole}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">From Company:</span>
                    <span className="ml-2 font-medium">{selectedTransfer.sourceCompanyName}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-3">What will be imported:</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-green-800">
                      Worker account & credentials
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Award className="w-4 h-4 text-green-600" />
                    <span className="text-green-800">
                      {selectedTransfer.certificatesCount} certificate(s) with expiry dates
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <BookOpen className="w-4 h-4 text-green-600" />
                    <span className="text-green-800">
                      {selectedTransfer.progressRecordsCount} training progress record(s)
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <ArrowRightLeft className="w-4 h-4 text-green-600" />
                    <span className="text-green-800">
                      All records will be marked as "Imported from {selectedTransfer.sourceCompanyName}"
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">
                  ✅ Compliance & Audit Trail
                </h4>
                <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
                  <li>Transfer approval will be logged with your admin credentials</li>
                  <li>Worker will receive email notification of successful transfer</li>
                  <li>Training history shows source company for compliance tracking</li>
                  <li>Certificate expiry dates are maintained for safety compliance</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowApprovalDialog(false);
                setSelectedTransfer(null);
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {processing ? 'Approving...' : 'Approve & Import Worker'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={showRejectionDialog} onOpenChange={setShowRejectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Reject Worker Transfer
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this transfer request
            </DialogDescription>
          </DialogHeader>

          {selectedTransfer && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded text-sm">
                <span className="font-medium">Worker:</span> {selectedTransfer.workerName} ({selectedTransfer.workerEmail})
              </div>

              <div className="space-y-2">
                <Label htmlFor="rejectionReason">Reason for Rejection *</Label>
                <Textarea
                  id="rejectionReason"
                  placeholder="E.g., Worker not authorized, duplicate account, position not available..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectionDialog(false);
                setSelectedTransfer(null);
                setRejectionReason('');
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={processing || !rejectionReason.trim()}
              variant="destructive"
            >
              <XCircle className="w-4 h-4 mr-2" />
              {processing ? 'Rejecting...' : 'Reject Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PendingTransfers;
